import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card } from "@/components/primitives";
import { initials } from "@/lib/format";
import { Radio, FileText, BarChart3, Building2, Users, MessageCircleMore, Info, ShieldCheck, MapPin, ChevronRight, CalendarDays, CalendarOff, FileSpreadsheet } from "lucide-react";

export default function More() {
  const { user, isAdmin, effectiveIsAdmin } = useAuth();
  const navigate = useNavigate();

  const tools = [
    { icon: Radio, label: "Fleet", hint: "Live driver map & status", to: "/fleet" },
    { icon: FileText, label: "Documents", hint: "PDFs, run sheets & forms", to: "/documents" },
    { icon: CalendarDays, label: "Schedules", hint: "Monthly rotation PDFs", to: "/schedules" },
    { icon: CalendarOff, label: "Time-Off", hint: "Request & track time off", to: "/time-off" },
    { icon: FileSpreadsheet, label: "Driver Logs", hint: "Pay-period logs & PDFs", to: "/driver-log" },
    ...(effectiveIsAdmin ? [
      { icon: BarChart3, label: "Reports", hint: "Monthly summaries & CSV", to: "/reports" },
      { icon: Building2, label: "Agencies", hint: "Funeral home directory", to: "/agencies" },
      { icon: Users, label: "Manage Users", hint: "Drivers, roles & regions", to: "/users" },
      { icon: MessageCircleMore, label: "SMS Logs", hint: "Dispatch alert delivery audit", to: "/sms-logs" },
    ] : []),
  ];

  return (
    <div>
      <PageHeader title="More tools" subtitle="Tools & destinations." />

      <Card className="p-4 flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center font-semibold">{initials(user?.name)}</div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-heading font-semibold text-curtron-ink truncate">{user?.name}</div>
            {effectiveIsAdmin && <span className="inline-flex items-center gap-1 text-[10px] uppercase bg-curtron-green/12 text-curtron-green rounded px-1.5 py-0.5"><ShieldCheck className="w-3 h-3" /> Admin</span>}
            {isAdmin && !effectiveIsAdmin && <span className="inline-flex items-center gap-1 text-[10px] uppercase bg-curtron-ochre/15 text-curtron-ochre rounded px-1.5 py-0.5"><Radio className="w-3 h-3" /> Driver view</span>}
          </div>
          <div className="text-xs text-curtron-muted truncate inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {user?.email}</div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        {tools.map((t) => (
          <button key={t.to} onClick={() => navigate(t.to)} data-testid={`more-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="text-left rounded-xl border border-curtron-line bg-curtron-surface p-4 hover:border-curtron-green hover:shadow-sm transition-all">
            <span className="w-10 h-10 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center mb-3"><t.icon className="w-5 h-5" /></span>
            <div className="font-medium text-curtron-ink text-sm">{t.label}</div>
            <div className="text-xs text-curtron-muted mt-0.5">{t.hint}</div>
          </button>
        ))}
      </div>

      <Card className="mt-4 divide-y divide-curtron-line">
        <button onClick={() => navigate("/announcements")} data-testid="more-announcements" className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-curtron-bg transition-colors">
          <span className="w-8 h-8 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center"><Info className="w-4 h-4" /></span>
          <div className="flex-1"><div className="text-sm text-curtron-ink">Announcements</div><div className="text-xs text-curtron-muted">Team broadcasts</div></div>
          <ChevronRight className="w-4 h-4 text-curtron-muted" />
        </button>
      </Card>

      <p className="text-center text-[11px] text-curtron-muted mt-6">Curtron Hub · a service of Curtron Transport Services</p>
    </div>
  );
}
